import { Component, inject, signal, effect, ElementRef, viewChild, OnDestroy, computed } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { ChatService, ChatMessage, Chat } from './services/chat.service';
import { AuthService } from './auth.service';
import { collection, query, orderBy, onSnapshot, Unsubscribe, doc, DocumentSnapshot, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, handleFirestoreError, OperationType } from './firebase';
import { Header } from './header';
import { AuthProfile } from './models/auth.model';
import { ContactItem } from './contacts';

@Component({
  selector: 'app-chat',
  imports: [CommonModule, RouterModule, MatIconModule, Header],
  template: `
    <div class="h-[100dvh] w-full flex bg-[#efeae2] dark:bg-[#0b141a] transition-colors duration-500 overflow-hidden relative">
      
      <!-- WhatsApp Wallpaper Pattern -->
      <div class="absolute inset-0 opacity-15 dark:opacity-5 pointer-events-none bg-[radial-gradient(#075e54_1px,transparent_1px)] [background-size:16px_16px] z-0"></div>

      <!-- Sidebar (Desktop/Tablet) -->
      <aside class="flex flex-col w-full lg:w-[380px] shrink-0 border-r border-white/10 bg-light-surface/30 dark:bg-dark-surface/30 backdrop-blur-3xl z-50 transition-all duration-300"
             [class.max-lg:hidden]="chatId() !== null">
        <app-header 
          title="Messages" 
          subtitle="@messages" 
          iconName="forum"
          (back)="goBack()">
          <div actions class="flex items-center gap-1.5">
            <button 
              type="button"
              (click)="startSelfChat()"
              class="h-8 px-2.5 rounded-full bg-ios-blue/10 text-ios-blue flex items-center gap-1 hover:bg-ios-blue/20 transition-all active:scale-90 text-xs font-bold cursor-pointer"
              title="Message Yourself (Note to Self)">
              <mat-icon class="!text-[16px]">person</mat-icon>
              <span class="hidden sm:inline">Self Note</span>
            </button>
          </div>
        </app-header>
        
        <div class="p-6 pb-2">
          <div class="ios-input-container !h-10 !rounded-xl !px-4 bg-black/5 dark:bg-white/5 border-none">
            <mat-icon class="text-ios-subtext/40 mr-2">search</mat-icon>
            <input type="text" placeholder="Search" [value]="searchQuery()" (input)="searchQuery.set($any($event.target).value)" class="ios-input !text-sm">
          </div>
        </div>

        <!-- Chat Sidebar Tab Bar: All, Person, Organization, Contacts -->
        <div class="px-4 pb-2.5 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <button
            type="button"
            id="chat-sidebar-tab-all"
            (click)="chatSidebarTab.set('all')"
            class="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all duration-300 cursor-pointer shrink-0 border shadow-xs active:scale-95"
            [class.bg-ios-blue]="chatSidebarTab() === 'all'"
            [class.text-white]="chatSidebarTab() === 'all'"
            [class.border-ios-blue]="chatSidebarTab() === 'all'"
            [class.shadow-md]="chatSidebarTab() === 'all'"
            [class.shadow-ios-blue/20]="chatSidebarTab() === 'all'"
            [class.bg-black/5]="chatSidebarTab() !== 'all'"
            [class.dark:bg-white/5]="chatSidebarTab() !== 'all'"
            [class.text-zinc-600]="chatSidebarTab() !== 'all'"
            [class.dark:text-zinc-400]="chatSidebarTab() !== 'all'"
            [class.border-black/5]="chatSidebarTab() !== 'all'"
            [class.dark:border-white/10]="chatSidebarTab() !== 'all'"
          >
            <mat-icon class="!text-[15px] leading-none shrink-0">forum</mat-icon>
            <span>All</span>
          </button>

          <button
            type="button"
            id="chat-sidebar-tab-person"
            (click)="chatSidebarTab.set('person')"
            class="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all duration-300 cursor-pointer shrink-0 border shadow-xs active:scale-95"
            [class.bg-ios-blue]="chatSidebarTab() === 'person'"
            [class.text-white]="chatSidebarTab() === 'person'"
            [class.border-ios-blue]="chatSidebarTab() === 'person'"
            [class.shadow-md]="chatSidebarTab() === 'person'"
            [class.shadow-ios-blue/20]="chatSidebarTab() === 'person'"
            [class.bg-black/5]="chatSidebarTab() !== 'person'"
            [class.dark:bg-white/5]="chatSidebarTab() !== 'person'"
            [class.text-zinc-600]="chatSidebarTab() !== 'person'"
            [class.dark:text-zinc-400]="chatSidebarTab() !== 'person'"
            [class.border-black/5]="chatSidebarTab() !== 'person'"
            [class.dark:border-white/10]="chatSidebarTab() !== 'person'"
          >
            <mat-icon class="!text-[15px] leading-none shrink-0">person</mat-icon>
            <span>Person</span>
          </button>

          <button
            type="button"
            id="chat-sidebar-tab-organization"
            (click)="chatSidebarTab.set('organization')"
            class="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all duration-300 cursor-pointer shrink-0 border shadow-xs active:scale-95"
            [class.bg-ios-blue]="chatSidebarTab() === 'organization'"
            [class.text-white]="chatSidebarTab() === 'organization'"
            [class.border-ios-blue]="chatSidebarTab() === 'organization'"
            [class.shadow-md]="chatSidebarTab() === 'organization'"
            [class.shadow-ios-blue/20]="chatSidebarTab() === 'organization'"
            [class.bg-black/5]="chatSidebarTab() !== 'organization'"
            [class.dark:bg-white/5]="chatSidebarTab() !== 'organization'"
            [class.text-zinc-600]="chatSidebarTab() !== 'organization'"
            [class.dark:text-zinc-400]="chatSidebarTab() !== 'organization'"
            [class.border-black/5]="chatSidebarTab() !== 'organization'"
            [class.dark:border-white/10]="chatSidebarTab() !== 'organization'"
          >
            <mat-icon class="!text-[15px] leading-none shrink-0">corporate_fare</mat-icon>
            <span>Organization</span>
          </button>

          <button
            type="button"
            id="chat-sidebar-tab-contacts"
            (click)="chatSidebarTab.set('contacts')"
            class="px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all duration-300 cursor-pointer shrink-0 border shadow-xs active:scale-95"
            [class.bg-ios-blue]="chatSidebarTab() === 'contacts'"
            [class.text-white]="chatSidebarTab() === 'contacts'"
            [class.border-ios-blue]="chatSidebarTab() === 'contacts'"
            [class.shadow-md]="chatSidebarTab() === 'contacts'"
            [class.shadow-ios-blue/20]="chatSidebarTab() === 'contacts'"
            [class.bg-black/5]="chatSidebarTab() !== 'contacts'"
            [class.dark:bg-white/5]="chatSidebarTab() !== 'contacts'"
            [class.text-zinc-600]="chatSidebarTab() !== 'contacts'"
            [class.dark:text-zinc-400]="chatSidebarTab() !== 'contacts'"
            [class.border-black/5]="chatSidebarTab() !== 'contacts'"
            [class.dark:border-white/10]="chatSidebarTab() !== 'contacts'"
          >
            <mat-icon class="!text-[15px] leading-none shrink-0">contacts</mat-icon>
            <span>Contacts</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto no-scrollbar p-2 space-y-1">
          @if (chatSidebarTab() === 'contacts') {
            <!-- Contacts list in sidebar -->
            @if (filteredSidebarContacts().length > 0) {
              @for (contact of filteredSidebarContacts(); track contact.id) {
                <div class="relative group/contact flex items-center w-full rounded-2xl overflow-hidden">
                  <button 
                    type="button"
                    (click)="startChatWithContact(contact)"
                    class="flex-1 flex items-center gap-3 p-3 rounded-2xl transition-all duration-300 group relative overflow-hidden text-left hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
                  >
                    <div class="w-11 h-11 rounded-full overflow-hidden bg-ios-blue/10 shrink-0 border border-white/10 relative">
                      @if (contact.photoURL) {
                        <img [src]="contact.photoURL" [alt]="contact.displayName" class="w-full h-full object-cover" referrerpolicy="no-referrer">
                      } @else {
                        <div class="w-full h-full flex items-center justify-center font-bold text-sm text-ios-blue">
                          {{ (contact.displayName || 'C').charAt(0).toUpperCase() }}
                        </div>
                      }
                      @if (contact.isOnline) {
                        <div class="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-zinc-900 z-10"></div>
                      }
                    </div>

                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between mb-0.5">
                        <span class="font-bold truncate text-sm text-zinc-900 dark:text-white">{{ contact.displayName }}</span>
                        <span class="text-[10px] px-2 py-0.5 rounded-full font-medium bg-black/5 dark:bg-white/10 text-zinc-500 dark:text-zinc-400 shrink-0 ml-1">
                          {{ contact.category }}
                        </span>
                      </div>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate font-medium">
                        {{ contact.username ? '@' + contact.username : (contact.phoneNumber || contact.email || 'Contact') }}
                      </p>
                    </div>

                    <div class="shrink-0 ml-1 text-ios-blue opacity-60 group-hover:opacity-100">
                      <mat-icon class="!text-[20px]">chat</mat-icon>
                    </div>
                  </button>
                </div>
              }
            } @else {
              <div class="py-12 px-4 text-center">
                <mat-icon class="!text-[36px] text-zinc-400 mb-2">contacts</mat-icon>
                <p class="text-xs font-bold text-zinc-500 mb-2">No contacts found</p>
                <a routerLink="/contacts" class="text-xs font-bold text-ios-blue hover:underline inline-flex items-center gap-1">
                  <mat-icon class="!text-[14px]">person_add</mat-icon>
                  <span>Go to Contacts</span>
                </a>
              </div>
            }
          } @else {
            <!-- Chat threads list -->
            @if (filteredChats().length > 0) {
              @for (chat of filteredChats(); track chat.id) {
                <div class="relative group/chat flex items-center w-full rounded-2xl overflow-hidden">
                  <button 
                    [routerLink]="['/chat', chat.id]"
                    [queryParams]="{uid: chat.participantId, name: chat.participantName, photo: chat.participantPhoto}"
                    class="flex-1 flex items-center gap-3.5 p-3.5 rounded-2xl transition-all duration-300 group relative overflow-hidden text-left"
                    [class.bg-ios-blue]="chat.id === chatId()"
                    [class.text-white]="chat.id === chatId()"
                    [class.hover:bg-black/5]="chat.id !== chatId()"
                    [class.dark:hover:bg-white/5]="chat.id !== chatId()">
                    
                    <div class="w-12 h-12 rounded-full overflow-hidden bg-ios-blue/10 shrink-0 border-2 border-white/10 group-hover:border-white/30 transition-all relative">
                      @if (chat.participantPhoto) {
                        <img [src]="chat.participantPhoto" alt="" class="w-full h-full object-cover" referrerpolicy="no-referrer">
                      } @else {
                        <div class="w-full h-full flex items-center justify-center font-bold text-base" [class.text-ios-blue]="chat.id !== chatId()">
                          {{ (chat.participantName || 'U').charAt(0).toUpperCase() }}
                        </div>
                      }
                      @if (chat.isOnline) {
                        <div class="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-zinc-900 z-10"></div>
                      }
                    </div>

                    <div class="flex-1 min-w-0">
                      <div class="flex justify-between items-center mb-0.5">
                        <span class="font-bold truncate text-sm">{{ chat.participantName }}</span>
                        <span class="text-[10px] opacity-60 font-medium whitespace-nowrap ml-2">{{ formatDate(chat.updatedAt) | date:'shortTime' }}</span>
                      </div>
                      <p class="text-xs opacity-60 truncate font-medium">{{ chat.lastMessage || 'No messages' }}</p>
                    </div>

                    @if (chat.id === chatId()) {
                      <div class="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-l-full"></div>
                    }
                  </button>

                  <button
                    type="button"
                    (click)="$event.stopPropagation(); $event.preventDefault(); confirmDeleteChat(chat)"
                    class="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-ios-red hover:bg-ios-red/10 transition-all cursor-pointer shrink-0 mr-1 opacity-70 hover:opacity-100"
                    title="Delete Chat"
                    aria-label="Delete Chat"
                  >
                    <mat-icon class="!text-[18px]">delete</mat-icon>
                  </button>
                </div>
              }
            } @else {
              <div class="py-12 px-4 text-center">
                <mat-icon class="!text-[36px] text-zinc-400 mb-2">forum</mat-icon>
                <p class="text-xs font-bold text-zinc-500">
                  No {{ chatSidebarTab() === 'all' ? '' : chatSidebarTab() }} chats found
                </p>
              </div>
            }
          }
        </div>
      </aside>

      <!-- Main Chat Area -->
      <div class="flex-1 flex flex-col relative z-10 min-w-0 h-full"
           [class.max-lg:hidden]="!chatId()">
        
        <!-- Header -->
        <app-header 
          [title]="participantName() || 'Chat'" 
          [subtitle]="participantUsername() ? '@' + participantUsername() : '@chat'"
          [imageUrl]="participantPhoto()"
          [isVerified]="participantVerified()"
          (back)="goBack()">
          <div actions class="relative z-10 flex items-center gap-1 sm:gap-2 shrink-0">
            <button class="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-ios-blue hover:bg-black/5 dark:hover:bg-white/5 transition-colors active:scale-90">
               <mat-icon class="!text-[20px]">videocam</mat-icon>
            </button>
            <button class="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-ios-blue hover:bg-black/5 dark:hover:bg-white/5 transition-colors active:scale-90">
               <mat-icon class="!text-[20px]">call</mat-icon>
            </button>
          </div>
        </app-header>

        <!-- Messages Area -->
        <main class="flex-1 px-4 md:px-8 max-w-4xl mx-auto w-full flex flex-col-reverse gap-4 overflow-y-auto py-6 no-scrollbar" #messagesContainer>
          @for (group of groupedMessages(); track group.date) {

            @for (senderGroup of group.senderGroups; track senderGroup.senderId) {
              <div class="flex flex-col-reverse gap-1" [class.items-end]="senderGroup.senderId === currentUserId()" [class.items-start]="senderGroup.senderId !== currentUserId()">
                @for (msg of senderGroup.messages; track msg.id; let first = $first; let last = $last) {
                  <div class="flex flex-col animate-ios-fade relative group/msg" 
                       [class.items-end]="msg.senderId === currentUserId()" 
                       [class.items-start]="msg.senderId !== currentUserId()"
                       [class.mt-0.5]="!last"
                       [class.mt-4]="last">
                    
                    <div class="relative max-w-[85%] md:max-w-[75%] px-3.5 pt-2 pb-6 transition-all duration-300 shadow-xs flex flex-col border border-black/5 dark:border-white/5 cursor-pointer select-none"
                         (touchstart)="onMessageTouchStart($event, msg)"
                         (touchend)="onMessageTouchEnd()"
                         (touchmove)="onMessageTouchMove()"
                         (mousedown)="onMessageMouseDown($event, msg)"
                         (mouseup)="onMessageMouseUp()"
                         (mouseleave)="onMessageMouseLeave()"
                         (contextmenu)="onContextMenuPrevent($event, msg)"
                         [class.bg-[#005c4b]]="msg.senderId === currentUserId()"
                         [class.text-white]="msg.senderId === currentUserId()"
                         [class.bg-white]="msg.senderId !== currentUserId()"
                         [class.text-[#111b21]]="msg.senderId !== currentUserId()"
                         [class.rounded-2xl]="true"
                         [class.rounded-tr-xs]="msg.senderId === currentUserId() && last"
                         [class.rounded-tl-xs]="msg.senderId !== currentUserId() && last">
                      
                      <!-- Reactions Display -->
                      @if (msg.reactions && getReactionCount(msg.reactions) > 0) {
                        <div class="absolute -bottom-3 flex items-center gap-1 px-2 py-0.5 bg-white/95 dark:bg-zinc-800/95 backdrop-blur-md rounded-full shadow-md border border-black/5 dark:border-white/10 z-20"
                             [class.right-2]="msg.senderId === currentUserId()"
                             [class.left-2]="msg.senderId !== currentUserId()">
                          @for (reaction of getReactionsList(msg.reactions); track reaction.emoji) {
                            <span class="text-xs flex items-center gap-0.5">
                              {{ reaction.emoji }} <span class="text-[9px] font-bold opacity-70">{{ reaction.count }}</span>
                            </span>
                          }
                        </div>
                      }

                      <!-- Quick Actions (Hover) -->
                      <div class="absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-all duration-300 flex items-center gap-1 bg-white/95 dark:bg-zinc-800/95 backdrop-blur-xl rounded-full p-1 shadow-md border border-black/5 dark:border-white/10 z-30"
                           [class.-left-[112px]]="msg.senderId === currentUserId()"
                           [class.-right-[112px]]="msg.senderId !== currentUserId()">
                        <button (click)="replyTo(msg)" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-[#00a884] mx-0.5" title="Reply">
                          <mat-icon class="text-[16px]">reply</mat-icon>
                        </button>
                        @if (msg.senderId === currentUserId()) {
                          <button (click)="editMessage(msg)" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-[#00a884]" title="Edit">
                            <mat-icon class="text-[16px]">edit</mat-icon>
                          </button>
                          <button (click)="deleteMessage(msg)" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-ios-red mr-0.5" title="Delete">
                            <mat-icon class="text-[16px]">delete</mat-icon>
                          </button>
                        } @else {
                          <div class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-[#00a884] mx-0.5 relative group/react">
                            <mat-icon class="text-[16px]">add_reaction</mat-icon>
                             <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/react:flex items-center gap-1 bg-white dark:bg-zinc-800 p-1 rounded-full shadow-lg border border-black/5 dark:border-white/10">
                               @for (emoji of ['👍', '❤️', '😂', '😮', '😢', '🙏']; track emoji) {
                                 <button (click)="$event.stopPropagation(); toggleReaction(msg, emoji)" class="w-7 h-7 flex items-center justify-center hover:scale-125 transition-transform">{{ emoji }}</button>
                               }
                             </div>
                          </div>
                        }
                      </div>

                      @if (msg.replyToId) {
                        @if (getRepliedMessage(msg.replyToId); as repliedMsg) {
                          <div class="text-[11px] opacity-80 mb-2 flex flex-col gap-0.5 p-2 rounded-lg bg-black/10 dark:bg-white/10 border-l-4 border-[#00a884]">
                            <span class="font-bold text-[#00a884] dark:text-[#25d366] text-[10px]">
                              {{ repliedMsg.senderId === currentUserId() ? 'You' : participantName() }}
                            </span>
                            <span class="truncate">{{ repliedMsg.text || 'Attachment' }}</span>
                          </div>
                        }
                      }

                      @if (msg.isDeleted) {
                        <p class="text-[14px] italic opacity-50 flex items-center gap-1">
                          <mat-icon class="text-[14px]">block</mat-icon> This message was deleted
                        </p>
                      } @else {
                        <!-- 1. Document Attachment Preview -->
                        @if (msg.docUrl || msg.docName) {
                          <div class="mb-2 p-2.5 rounded-xl bg-black/10 dark:bg-white/10 flex items-center gap-3 border border-black/5 dark:border-white/10">
                            <div class="w-10 h-10 rounded-xl bg-[#5f66cd] text-white flex items-center justify-center shrink-0 shadow-xs">
                              <mat-icon class="!text-[22px]">description</mat-icon>
                            </div>
                            <div class="flex-1 min-w-0">
                              <p class="text-xs font-bold truncate">{{ msg.docName || 'Document.pdf' }}</p>
                              <p class="text-[10px] opacity-75 font-medium">{{ msg.docSize || 'PDF Document • 1.5 MB' }}</p>
                            </div>
                            @if (msg.docUrl) {
                              <a [href]="msg.docUrl" target="_blank" download class="w-8 h-8 rounded-full bg-black/10 dark:bg-white/10 hover:bg-black/20 text-zinc-700 dark:text-zinc-200 flex items-center justify-center transition-colors shrink-0" title="Download Document">
                                <mat-icon class="!text-[16px]">download</mat-icon>
                              </a>
                            }
                          </div>
                        }

                        <!-- 2. Image Attachment Preview -->
                        @if (msg.imageUrls && msg.imageUrls.length > 1) {
                          <div class="grid grid-cols-2 gap-1.5 mb-2 rounded-xl overflow-hidden shadow-xs border border-black/5 dark:border-white/5">
                            @for (img of msg.imageUrls; track img) {
                              <div class="aspect-square overflow-hidden bg-black/10 cursor-pointer" (click)="previewingImage.set(img)">
                                <img [src]="img" alt="Attachment" class="w-full h-full object-cover hover:scale-105 transition-transform" referrerpolicy="no-referrer" />
                              </div>
                            }
                          </div>
                        } @else if (msg.imageUrl || (msg.imageUrls && msg.imageUrls.length === 1)) {
                          <div class="rounded-xl overflow-hidden mb-2 shadow-xs border border-black/5 dark:border-white/5 cursor-pointer max-w-sm" (click)="previewingImage.set(msg.imageUrl || msg.imageUrls![0])">
                            <img [src]="msg.imageUrl || msg.imageUrls![0]" alt="Attachment" class="max-w-full h-auto object-cover hover:scale-[1.02] transition-transform" referrerpolicy="no-referrer" />
                          </div>
                        }

                        <!-- 3. Video Attachment Preview -->
                        @if (msg.videoUrl) {
                          <div class="rounded-xl overflow-hidden mb-2 shadow-xs border border-black/5 dark:border-white/5 bg-black/20 max-w-sm">
                            <video [src]="msg.videoUrl" controls class="w-full max-h-60 object-cover rounded-xl"></video>
                          </div>
                        }

                        <!-- 4. Audio / Voice Note Preview -->
                        @if (msg.audioUrl) {
                          <div class="flex items-center gap-3 min-w-[220px] sm:min-w-[260px] py-1.5 px-1 mb-1">
                            <button type="button" class="w-10 h-10 rounded-full bg-[#00a884] text-white flex items-center justify-center shadow-xs hover:scale-105 active:scale-95 transition-all shrink-0 cursor-pointer">
                              <mat-icon class="!text-[22px]">play_arrow</mat-icon>
                            </button>
                            <div class="flex-1 flex flex-col gap-1">
                              <div class="h-6 flex items-center gap-0.5">
                                @for (i of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18]; track i) {
                                  <div class="w-1 bg-black/30 dark:bg-white/40 rounded-full" [style.height.px]="getWaveformHeight(i)"></div>
                                }
                              </div>
                              <div class="flex items-center justify-between text-[10px] opacity-75 font-medium">
                                <span>0:14</span>
                                <span class="flex items-center gap-0.5 text-[#00a884] dark:text-[#25d366]">
                                  <mat-icon class="!text-[12px]">mic</mat-icon>
                                  Voice Note
                                </span>
                              </div>
                            </div>
                          </div>
                        }

                        <!-- 5. Location Attachment Preview -->
                        @if (msg.location || (msg.text && msg.text.includes('📍'))) {
                          <div class="mb-2 rounded-xl overflow-hidden border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 max-w-xs shadow-xs">
                            <div class="h-28 bg-gradient-to-tr from-emerald-600 via-teal-500 to-sky-600 relative flex items-center justify-center p-4 text-white">
                              <div class="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg animate-bounce">
                                <mat-icon class="!text-[22px]">location_on</mat-icon>
                              </div>
                              <div class="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-xs text-[10px] font-bold">
                                Live GPS
                              </div>
                            </div>
                            <div class="p-2.5">
                              <p class="text-xs font-bold truncate">{{ msg.location?.title || 'Shared Location' }}</p>
                              <p class="text-[10px] opacity-70 font-medium truncate">{{ msg.location?.address || 'San Francisco, CA (37.7749° N, 122.4194° W)' }}</p>
                              <a [href]="'https://maps.google.com/?q=' + (msg.location?.lat || '37.7749') + ',' + (msg.location?.lng || '-122.4194')"
                                 target="_blank"
                                 class="mt-2 text-[11px] font-bold text-[#00a884] dark:text-[#25d366] flex items-center gap-1 hover:underline">
                                <mat-icon class="!text-[14px]">map</mat-icon>
                                <span>Open in Google Maps</span>
                              </a>
                            </div>
                          </div>
                        }

                        <!-- 6. Contact Card Attachment Preview -->
                        @if (msg.contact || (msg.text && msg.text.includes('👤 Contact'))) {
                          <div class="mb-2 rounded-xl border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 max-w-xs overflow-hidden shadow-xs">
                            <div class="p-3 flex items-center gap-3 border-b border-black/5 dark:border-white/5">
                              <div class="w-10 h-10 rounded-full bg-[#0099ff] text-white font-black flex items-center justify-center text-sm shadow-xs shrink-0">
                                {{ (msg.contact?.name || 'K').charAt(0).toUpperCase() }}
                              </div>
                              <div class="min-w-0 flex-1">
                                <p class="text-xs font-bold truncate">{{ msg.contact?.name || 'Kicgle Support' }}</p>
                                <p class="text-[10px] opacity-70 font-medium truncate">{{ msg.contact?.phone || '(555) 019-2831' }}</p>
                              </div>
                            </div>
                            <div class="grid grid-cols-2 divide-x divide-black/5 dark:divide-white/5 text-[11px] font-bold text-[#00a884] dark:text-[#25d366] text-center">
                              <button type="button" class="py-2 hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer">
                                Message
                              </button>
                              <button type="button" class="py-2 hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer">
                                Add Contact
                              </button>
                            </div>
                          </div>
                        }

                        <!-- Text Content -->
                        @if (msg.text && !msg.text.startsWith('📍') && !msg.text.startsWith('👤')) {
                          <p class="text-[15px] leading-snug whitespace-pre-wrap select-text pr-12 pb-1">{{ msg.text }}</p>
                        }
                      }

                      <!-- Timestamp & Tick Marks (Bottom Right) -->
                      <div class="absolute bottom-1 right-2.5 flex items-center justify-end gap-1 select-none">
                        <span class="text-[10px] font-medium leading-none"
                              [class.text-white/80]="msg.senderId === currentUserId()"
                              [class.text-gray-500]="msg.senderId !== currentUserId()">
                          {{ formatDate(msg.createdAt) | date:'h:mm a' }}
                        </span>
                        <!-- Tick marks strictly ONLY on sent messages -->
                        @if (msg.senderId === currentUserId()) {
                          <mat-icon class="!text-[14px] !w-[14px] !h-[14px] leading-none"
                                    [class.text-[#53bdeb]]="msg.status === 'seen'"
                                    [class.text-white/70]="msg.status !== 'seen'">
                            {{ msg.status === 'seen' ? 'done_all' : 'done' }}
                          </mat-icon>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
            
            <div class="flex items-center gap-4 my-6">
              <div class="h-px flex-1 bg-black/5 dark:bg-white/5"></div>
              <div class="text-[10px] font-black uppercase tracking-[0.2em] opacity-30">{{ group.date }}</div>
              <div class="h-px flex-1 bg-black/5 dark:bg-white/5"></div>
            </div>
          }
          
          <!-- Typing Indicator -->
          @if (isTyping()) {
            <div class="flex items-center gap-2 animate-ios-fade">
              <div class="w-10 h-10 rounded-full bg-ios-blue/10 flex items-center justify-center overflow-hidden">
                @if (participantPhoto()) {
                  <img [src]="participantPhoto()" alt="" class="w-full h-full object-cover" />
                } @else {
                  <span class="text-ios-blue font-bold">{{ participantName()?.charAt(0) }}</span>
                }
              </div>
              <div class="bg-light-surface/80 dark:bg-dark-surface/80 backdrop-blur-xl p-3 rounded-[20px] rounded-tl-[4px] shadow-sm flex items-center gap-1">
                <div class="w-1.5 h-1.5 bg-ios-blue/40 rounded-full animate-bounce"></div>
                <div class="w-1.5 h-1.5 bg-ios-blue/40 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                <div class="w-1.5 h-1.5 bg-ios-blue/40 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>
              </div>
            </div>
          }
        </main>

        <!-- Input Area (WhatsApp Style) -->
        <footer class="flex-none shrink-0 z-[100] px-3 py-2 sm:px-4 sm:py-3 bg-[#f0f2f5] dark:bg-[#111b21] border-t border-black/5 dark:border-white/10 relative select-none">
          <div class="max-w-4xl mx-auto w-full relative">
            @if (replyingToMsg) {
              <div class="mb-2 p-2.5 bg-black/5 dark:bg-white/5 rounded-xl flex items-center justify-between border-l-4 border-[#00a884] animate-ios-slide backdrop-blur-md">
                <div class="flex flex-col overflow-hidden">
                  <span class="text-[11px] font-bold text-[#00a884] flex items-center gap-1">
                    <mat-icon class="!text-[14px]">reply</mat-icon>
                    Replying to {{ replyingToMsg.senderId === currentUserId() ? 'Yourself' : participantName() }}
                  </span>
                  <span class="text-xs truncate opacity-80 font-medium">{{ replyingToMsg.text || 'Attachment' }}</span>
                </div>
                <button (click)="replyingToMsg = null" class="w-7 h-7 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-white transition-colors cursor-pointer">
                  <mat-icon class="!text-[16px]">close</mat-icon>
                </button>
              </div>
            }

            @if (isUploading()) {
              <div class="mb-2.5 p-2 px-3.5 rounded-xl bg-[#00a884]/15 text-[#00a884] dark:text-[#25d366] text-xs font-bold flex items-center gap-2 animate-pulse border border-[#00a884]/30">
                <div class="w-3.5 h-3.5 border-2 border-[#00a884]/30 border-t-[#00a884] rounded-full animate-spin"></div>
                <span>Uploading attachment to Firebase Storage...</span>
              </div>
            }

            <!-- WhatsApp Attachment Menu Tray (6 Distinct Options) -->
            @if (showAttachmentMenu) {
              <div class="absolute bottom-14 left-0 sm:left-2 z-[500] p-4 sm:p-5 rounded-2xl bg-white dark:bg-[#202c33] shadow-2xl border border-black/10 dark:border-white/10 animate-ios-slide-up grid grid-cols-3 gap-4 sm:gap-6 min-w-[280px] sm:min-w-[320px]">
                <!-- 1. Document -->
                <button (click)="triggerAttachment('document')" type="button" class="flex flex-col items-center gap-1.5 group cursor-pointer active:scale-95 transition-transform">
                  <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#5f66cd] text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <mat-icon class="!text-[24px]">description</mat-icon>
                  </div>
                  <span class="text-[11px] font-medium text-zinc-700 dark:text-zinc-200">Document</span>
                </button>

                <!-- 2. Image -->
                <button (click)="triggerAttachment('image')" type="button" class="flex flex-col items-center gap-1.5 group cursor-pointer active:scale-95 transition-transform">
                  <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#ec407a] text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <mat-icon class="!text-[24px]">photo_camera</mat-icon>
                  </div>
                  <span class="text-[11px] font-medium text-zinc-700 dark:text-zinc-200">Image</span>
                </button>

                <!-- 3. Video -->
                <button (click)="triggerAttachment('video')" type="button" class="flex flex-col items-center gap-1.5 group cursor-pointer active:scale-95 transition-transform">
                  <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#ac44ab] text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <mat-icon class="!text-[24px]">videocam</mat-icon>
                  </div>
                  <span class="text-[11px] font-medium text-zinc-700 dark:text-zinc-200">Video</span>
                </button>

                <!-- 4. Audio -->
                <button (click)="triggerAttachment('audio')" type="button" class="flex flex-col items-center gap-1.5 group cursor-pointer active:scale-95 transition-transform">
                  <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#f26722] text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <mat-icon class="!text-[24px]">headset</mat-icon>
                  </div>
                  <span class="text-[11px] font-medium text-zinc-700 dark:text-zinc-200">Audio</span>
                </button>

                <!-- 5. Location -->
                <button (click)="triggerAttachment('location')" type="button" class="flex flex-col items-center gap-1.5 group cursor-pointer active:scale-95 transition-transform">
                  <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#0f9d58] text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <mat-icon class="!text-[24px]">location_on</mat-icon>
                  </div>
                  <span class="text-[11px] font-medium text-zinc-700 dark:text-zinc-200">Location</span>
                </button>

                <!-- 6. Contact -->
                <button (click)="triggerAttachment('contact')" type="button" class="flex flex-col items-center gap-1.5 group cursor-pointer active:scale-95 transition-transform">
                  <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#0099ff] text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                    <mat-icon class="!text-[24px]">person</mat-icon>
                  </div>
                  <span class="text-[11px] font-medium text-zinc-700 dark:text-zinc-200">Contact</span>
                </button>
              </div>
            }

            <!-- Emoji Picker Popover -->
            @if (showEmojiPicker) {
              <div class="absolute bottom-14 left-8 z-[500] p-3 rounded-2xl bg-white dark:bg-[#202c33] shadow-xl border border-black/10 dark:border-white/10 flex items-center gap-2 animate-ios-slide-up">
                @for (e of ['😀', '😂', '😍', '👍', '❤️', '🔥', '🙏', '🎉', '😊', '🙌']; track e) {
                  <button type="button" (click)="addEmoji(e)" class="text-xl hover:scale-125 transition-transform p-1 cursor-pointer">
                    {{ e }}
                  </button>
                }
              </div>
            }

            <!-- File Previews -->
            @if (selectedFiles.length > 0) {
              <div class="mb-3 flex gap-2.5 overflow-x-auto pb-1 no-scrollbar">
                @for (file of selectedFiles; track file.name; let i = $index) {
                  <div class="relative w-16 h-16 shrink-0 rounded-xl overflow-hidden border border-black/10 dark:border-white/10 shadow-md group">
                    @if (file.type.startsWith('image/')) {
                      <img [src]="getFilePreview(file)" class="w-full h-full object-cover" alt="Preview" />
                    } @else {
                      <div class="w-full h-full bg-[#00a884]/10 flex flex-col items-center justify-center p-1 text-center">
                        <mat-icon class="text-[#00a884] !text-[18px]">description</mat-icon>
                        <span class="text-[8px] font-medium truncate w-full opacity-80">{{ file.name }}</span>
                      </div>
                    }
                    <button (click)="removeFile(i)" class="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-ios-red transition-colors cursor-pointer">
                      <mat-icon class="!text-[10px]">close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }

            <!-- Input Controls Bar -->
            <div class="flex items-center gap-1.5 sm:gap-2 relative">
              <!-- Hidden Inputs for Attachments -->
              <input type="file" #fileInput class="hidden" (change)="onFileSelected($event)" multiple>
              <input type="file" #docFileInput class="hidden" (change)="onFileSelected($event)" accept=".pdf,.doc,.docx,.txt,.xls,.xlsx">
              <input type="file" #imageFileInput class="hidden" (change)="onFileSelected($event)" accept="image/*" multiple>
              <input type="file" #videoFileInput class="hidden" (change)="onFileSelected($event)" accept="video/*">
              <input type="file" #audioFileInput class="hidden" (change)="onFileSelected($event)" accept="audio/*">
              
              <!-- Voice Recording Active Bar (WhatsApp Audio Note Interface) -->
              @if (isRecording()) {
                <div class="flex-1 h-12 bg-white dark:bg-[#202c33] rounded-full flex items-center px-3 sm:px-4 gap-2 sm:gap-3 border border-black/10 dark:border-white/10 shadow-lg animate-ios-slide-up select-none">
                  
                  <!-- 1. Delete / Trash Button (Cancels/deletes recording) -->
                  <button 
                    (click)="cancelRecording()" 
                    type="button" 
                    class="w-9 h-9 rounded-full hover:bg-ios-red/10 text-zinc-400 hover:text-ios-red flex items-center justify-center transition-all cursor-pointer active:scale-90 shrink-0" 
                    title="Delete Recording"
                  >
                    <mat-icon class="!text-[20px]">delete</mat-icon>
                  </button>

                  <!-- 2. Timer & Live Waveform Indicator -->
                  <div class="flex-1 flex items-center gap-2 min-w-0">
                    <div class="w-2.5 h-2.5 bg-ios-red rounded-full shrink-0" [class.animate-pulse]="!isRecordingPaused()"></div>
                    <span class="font-mono text-xs font-bold text-zinc-800 dark:text-zinc-100 min-w-[36px]">{{ recordingTime() }}</span>
                    
                    <!-- Audio Waveform Live Visualizer -->
                    <div class="flex-1 h-5 flex items-center gap-0.5 overflow-hidden">
                      @for (i of [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]; track i) {
                        <div class="w-1 bg-[#00a884] rounded-full transition-all duration-300"
                             [style.height.px]="isRecordingPaused() ? 4 : getWaveformHeight(i)"
                             [class.opacity-40]="isRecordingPaused()"></div>
                      }
                    </div>
                  </div>

                  <!-- 3. Pause / Resume Recording Button -->
                  <button 
                    (click)="togglePauseRecording()" 
                    type="button" 
                    class="w-9 h-9 rounded-full bg-black/5 dark:bg-white/10 text-zinc-700 dark:text-zinc-200 hover:bg-black/10 dark:hover:bg-white/20 flex items-center justify-center transition-all cursor-pointer active:scale-90 shrink-0" 
                    [title]="isRecordingPaused() ? 'Resume Recording' : 'Pause Recording'"
                  >
                    <mat-icon class="!text-[20px]">{{ isRecordingPaused() ? 'mic' : 'pause' }}</mat-icon>
                  </button>

                  <!-- 4. Stop & Send Recording Button -->
                  <button 
                    (click)="stopRecording()" 
                    type="button" 
                    class="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#00a884] hover:bg-[#008f70] text-white flex items-center justify-center shadow-md active:scale-90 transition-all cursor-pointer shrink-0" 
                    title="Stop & Send Voice Note"
                  >
                    <mat-icon class="!text-[20px] ml-0.5">send</mat-icon>
                  </button>

                </div>
              } @else {

                <!-- WhatsApp Paperclip Attachment Button -->
                <button type="button"
                        (click)="showAttachmentMenu = !showAttachmentMenu"
                        class="w-10 h-10 rounded-full flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-all active:scale-90 shrink-0 cursor-pointer"
                        [class.rotate-45]="showAttachmentMenu"
                        title="Attach File">
                  <mat-icon class="!text-[22px] -rotate-45">attach_file</mat-icon>
                </button>

                <!-- Text Field Pill Container -->
                <div class="flex-1 flex items-center gap-2 bg-white dark:bg-[#2a3942] rounded-3xl px-3.5 py-1.5 shadow-xs border border-black/5 dark:border-white/5 min-h-[42px]">
                  <!-- Emoji Button -->
                  <button type="button"
                          (click)="toggleEmojiPicker()"
                          class="text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors shrink-0 cursor-pointer"
                          title="Emojis">
                    <mat-icon class="!text-[22px]">sentiment_satisfied</mat-icon>
                  </button>

                  <textarea 
                    [value]="newMessage" 
                    (input)="newMessage = $any($event.target).value; onTyping(); adjustTextareaHeight(messageInput)"
                    (keydown.enter)="sendMessage($event)"
                    placeholder="Type a message" 
                    class="flex-1 bg-transparent border-none outline-none resize-none max-h-24 py-1 text-sm sm:text-base text-zinc-900 dark:text-white placeholder:text-zinc-400 font-sans"
                    rows="1"
                    #messageInput>
                  </textarea>
                </div>

                <!-- Voice Recording Microphone or Send Button -->
                @if (!newMessage.trim() && selectedFiles.length === 0) {
                  <button type="button"
                          (click)="startRecording()"
                          class="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#00a884] hover:bg-[#008f70] text-white flex items-center justify-center shadow-md active:scale-90 transition-all shrink-0 cursor-pointer"
                          title="Voice Note">
                    <mat-icon class="!text-[20px] sm:!text-[22px]">mic</mat-icon>
                  </button>
                } @else {
                  <button type="button"
                          (click)="sendMessage()"
                          class="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#00a884] hover:bg-[#008f70] text-white flex items-center justify-center shadow-md active:scale-90 transition-all shrink-0 cursor-pointer"
                          title="Send Message">
                    <mat-icon class="!text-[20px] sm:!text-[22px] ml-0.5">send</mat-icon>
                  </button>
                }

              }
            </div>
          </div>
        </footer>

        <!-- Future Vision Modal -->
        @if (showFutureModal()) {
          <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 md:p-6 animate-ios-fade">
            <div class="absolute inset-0 bg-black/40 backdrop-blur-md" 
                 (click)="showFutureModal.set(false)"
                 (keydown.escape)="showFutureModal.set(false)"
                 tabindex="-1"
                 role="button"
                 aria-label="Close modal"></div>
            <div class="glass-card w-full max-w-lg p-6 md:p-8 !rounded-[40px] shadow-2xl relative z-10 border-white/30">
              <div class="flex items-center justify-between mb-8">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                    <mat-icon>auto_awesome</mat-icon>
                  </div>
                  <div>
                    <h2 class="text-2xl font-bold tracking-tight">Future Vision</h2>
                    <p class="text-xs opacity-50 font-medium uppercase tracking-widest">Relationship Roadmap</p>
                  </div>
                </div>
                <button (click)="showFutureModal.set(false)" class="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center hover:bg-black/10 transition-all">
                  <mat-icon>close</mat-icon>
                </button>
              </div>

              <div class="space-y-8">
                <div class="space-y-3">
                  <label for="vision-input" class="text-[10px] font-black uppercase tracking-widest opacity-40 ml-1">The Vision</label>
                  <textarea 
                    id="vision-input"
                    [value]="futureVision"
                    (input)="futureVision = $any($event.target).value"
                    placeholder="Describe the long-term goal of this conversation or relationship..."
                    class="w-full bg-black/5 dark:bg-white/5 border-none rounded-2xl p-4 text-sm min-h-[120px] focus:ring-2 ring-ios-blue/20 outline-none transition-all"
                  ></textarea>
                </div>

                <div class="space-y-4">
                  <span class="text-[10px] font-black uppercase tracking-widest opacity-40 ml-1 block">Key Milestones</span>
                  <div class="space-y-3">
                    @for (milestone of futureMilestones; track $index) {
                      <div class="flex items-center gap-3 animate-ios-slide">
                        <div class="w-8 h-8 rounded-full bg-ios-blue/10 text-ios-blue flex items-center justify-center shrink-0">
                          <mat-icon class="text-[16px]">check_circle</mat-icon>
                        </div>
                        <input 
                          [value]="futureMilestones[$index]"
                          (input)="futureMilestones[$index] = $any($event.target).value"
                          class="flex-1 bg-transparent border-none outline-none text-sm font-medium"
                          placeholder="Enter milestone..."
                        >
                        <button (click)="removeMilestone($index)" class="text-ios-red/40 hover:text-ios-red transition-colors">
                          <mat-icon class="text-[18px]">delete</mat-icon>
                        </button>
                      </div>
                    }
                    <button (click)="addMilestone()" class="w-full py-3 rounded-2xl border-2 border-dashed border-ios-blue/20 text-ios-blue text-xs font-bold hover:bg-ios-blue/5 transition-all flex items-center justify-center gap-2">
                      <mat-icon class="text-[18px]">add</mat-icon>
                      Add Milestone
                    </button>
                  </div>
                </div>

                <button (click)="saveFutureVision()" class="w-full py-4 rounded-2xl bg-ios-blue text-white font-bold shadow-xl shadow-ios-blue/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2">
                  <mat-icon>save</mat-icon>
                  Save Vision
                </button>
              </div>
            </div>
          </div>
        }

        <!-- Image Lightbox Modal -->
        @if (previewingImage()) {
          <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-ios-fade"
               (click)="previewingImage.set(null)"
               (keydown.escape)="previewingImage.set(null)">
            <button type="button" (click)="previewingImage.set(null)" class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/40 transition-colors z-10 cursor-pointer">
              <mat-icon class="!text-[24px]">close</mat-icon>
            </button>
            <img [src]="previewingImage()" class="max-w-full max-h-[90vh] object-contain rounded-2xl shadow-2xl" alt="Full Preview" (click)="$event.stopPropagation()" referrerpolicy="no-referrer" />
          </div>
        }

        <!-- WhatsApp Long-Press Context Menu Modal -->
        @if (selectedContextMsg(); as ctxMsg) {
          <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade select-none">
            <!-- Backdrop -->
            <div 
              class="absolute inset-0 bg-black/60 backdrop-blur-md"
              (click)="closeContextMenu()"
              (keydown.escape)="closeContextMenu()"
              tabindex="-1"
              role="button"
              aria-label="Close menu backdrop"
            ></div>

            <!-- Floating Action Container -->
            <div class="relative z-10 w-full max-w-xs flex flex-col gap-2.5 animate-ios-slide-up" (click)="$event.stopPropagation()">
              
              <!-- 1. Quick Reaction Emoji Pill -->
              <div class="bg-white dark:bg-[#202c33] p-2 px-3 rounded-full shadow-2xl border border-black/10 dark:border-white/10 flex items-center justify-between">
                @for (emoji of ['👍', '❤️', '😂', '😮', '😢', '🙏']; track emoji) {
                  <button 
                    type="button"
                    (click)="toggleReaction(ctxMsg, emoji); closeContextMenu()" 
                    class="text-2xl hover:scale-130 active:scale-95 transition-transform p-1 cursor-pointer"
                  >
                    {{ emoji }}
                  </button>
                }
              </div>

              <!-- 2. Selected Message Highlight Preview -->
              <div class="p-3.5 rounded-2xl bg-white/95 dark:bg-[#202c33]/95 backdrop-blur-xl border border-black/10 dark:border-white/10 shadow-lg text-xs font-medium text-zinc-800 dark:text-zinc-100 max-h-28 overflow-y-auto">
                <span class="font-bold text-[#00a884] block mb-1 text-[11px]">
                  {{ ctxMsg.senderId === currentUserId() ? 'You' : participantName() }}
                </span>
                <span class="truncate block opacity-90 font-sans leading-relaxed">
                  {{ ctxMsg.text || (ctxMsg.docName ? '📄 ' + ctxMsg.docName : (ctxMsg.audioUrl ? '🎤 Voice Note' : (ctxMsg.imageUrl ? '📷 Photo' : '📎 Attachment'))) }}
                </span>
              </div>

              <!-- 3. WhatsApp Hierarchy Action Menu (Reply, Forward, Copy, Delete) -->
              <div class="bg-white dark:bg-[#202c33] rounded-2xl shadow-2xl border border-black/10 dark:border-white/10 overflow-hidden divide-y divide-black/5 dark:divide-white/5 font-sans">
                
                <!-- Reply -->
                <button 
                  type="button"
                  (click)="replyTo(ctxMsg); closeContextMenu()" 
                  class="w-full px-4 py-3 flex items-center justify-between text-xs font-semibold text-zinc-800 dark:text-zinc-100 hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer active:bg-black/10"
                >
                  <span>Reply</span>
                  <mat-icon class="!text-[18px] text-[#00a884]">reply</mat-icon>
                </button>

                <!-- Forward -->
                <button 
                  type="button"
                  (click)="forwardMessage(ctxMsg); closeContextMenu()" 
                  class="w-full px-4 py-3 flex items-center justify-between text-xs font-semibold text-zinc-800 dark:text-zinc-100 hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer active:bg-black/10"
                >
                  <span>Forward</span>
                  <mat-icon class="!text-[18px] text-zinc-500 dark:text-zinc-400">shortcut</mat-icon>
                </button>

                <!-- Copy -->
                <button 
                  type="button"
                  (click)="copyMessageText(ctxMsg); closeContextMenu()" 
                  class="w-full px-4 py-3 flex items-center justify-between text-xs font-semibold text-zinc-800 dark:text-zinc-100 hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer active:bg-black/10"
                >
                  <span>Copy</span>
                  <mat-icon class="!text-[18px] text-zinc-500 dark:text-zinc-400">content_copy</mat-icon>
                </button>

                <!-- Delete (for user's own sent messages) -->
                @if (ctxMsg.senderId === currentUserId()) {
                  <button 
                    type="button"
                    (click)="deleteMessage(ctxMsg); closeContextMenu()" 
                    class="w-full px-4 py-3 flex items-center justify-between text-xs font-semibold text-ios-red hover:bg-ios-red/10 transition-colors cursor-pointer active:bg-ios-red/20"
                  >
                    <span>Delete</span>
                    <mat-icon class="!text-[18px] text-ios-red">delete</mat-icon>
                  </button>
                }
              </div>

            </div>
          </div>
        }
      </div>
    </div>
  `,

})
export class ChatView implements OnDestroy {
  previewingImage = signal<string | null>(null);
  isUploading = signal<boolean>(false);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private router = inject(Router);
  private chatService = inject(ChatService);
  private authService = inject(AuthService);

  showBotSettings = signal<boolean>(false);
  participantProfile = signal<AuthProfile | null>(null);

  canEditBotSettings(): boolean {
    return true;
  }

  openBotSettings() {
    const pid = this.participantId();
    if (!this.participantProfile() && pid) {
      this.participantProfile.set({
        uid: pid,
        displayName: this.participantName() || 'Assistant',
        email: '',
        photoURL: this.participantPhoto() || '',
        bio: 'Official Assistant'
      } as AuthProfile);
    }
    this.showBotSettings.set(true);
  }

  chatId = signal<string | null>(null);
  participantId = signal<string | null>(null);
  participantName = signal<string | null>(null);
  participantPhoto = signal<string | null>(null);
  participantUsername = signal<string | null>(null);
  
  messages = signal<ChatMessage[]>([]);
  newMessage = '';
  selectedFiles: File[] = [];
  currentUserId = signal<string | null>(null);
  private unsubscribeMessages: Unsubscribe | null = null;
  private unsubscribeOnlineStatus: Unsubscribe | null = null;
  private unsubscribeChatData: Unsubscribe | null = null;
  private unsubscribeChatsList: (() => void) | null = null;

  replyingToMsg: ChatMessage | null = null;
  editingMsg: ChatMessage | null = null;
  forwardingMsg = signal<ChatMessage | null>(null);
  availableChats = signal<Chat[]>([]);
  
  searchQuery = signal('');
  chatSidebarTab = signal<'all' | 'person' | 'organization' | 'contacts'>('all');
  userContacts = signal<ContactItem[]>([]);
  private unsubscribeContacts: Unsubscribe | null = null;

  chatAccountTypes = signal<Record<string, string>>({});
  private fetchedChatAccountUids = new Set<string>();

  getChatAccountType(chat: Chat): string {
    const uid = chat.participantId || (chat as { uid?: string }).uid;
    if (uid) {
      const lookedUpType = this.chatAccountTypes()[uid];
      if (lookedUpType !== undefined) {
        return lookedUpType;
      }
    }
    return (chat.participantAccountType || '').toString().trim();
  }

  isPersonChat(chat: Chat): boolean {
    const accountType = this.getChatAccountType(chat);
    return accountType === 'Person' || accountType.toLowerCase() === 'person';
  }

  isOrganizationChat(chat: Chat): boolean {
    const accountType = this.getChatAccountType(chat);
    return accountType === 'Organization' || accountType.toLowerCase() === 'organization';
  }

  filteredChats = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const tab = this.chatSidebarTab();
    const chats = this.availableChats().filter(c => 
      c.lastMessage && 
      (c.participantName?.toLowerCase() ?? '').includes(query)
    );

    if (tab === 'person') {
      return chats.filter(c => this.isPersonChat(c));
    }
    if (tab === 'organization') {
      return chats.filter(c => this.isOrganizationChat(c));
    }
    // 'all' tab displays both person and organization chats
    return chats.filter(c => this.isPersonChat(c) || this.isOrganizationChat(c));
  });

  filteredSidebarContacts = computed(() => {
    const query = this.searchQuery().toLowerCase();
    return this.userContacts().filter(c =>
      (c.displayName?.toLowerCase() ?? '').includes(query) ||
      (c.username?.toLowerCase() ?? '').includes(query) ||
      (c.phoneNumber ?? '').includes(query)
    );
  });

  isForwarding = signal(false);
  isOnline = signal<boolean>(false);
  participantVerified = signal<boolean>(false);
  isTyping = signal<boolean>(false);
  isRecording = signal<boolean>(false);
  isRecordingPaused = signal<boolean>(false);
  recordingTime = signal<string>('0:00');
  selectedContextMsg = signal<ChatMessage | null>(null);
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private touchStartX = 0;
  private touchStartY = 0;
  private recordingStartTime = 0;
  private recordingTimer: ReturnType<typeof setInterval> | null = null;

  showAttachmentMenu = false;
  showEmojiPicker = false;
  showFutureModal = signal(false);
  futureVision = '';
  futureMilestones: string[] = [];
  showScheduleMenu = false;
  scheduledDateTime = '';
  currentTime = signal(Date.now());
  private timeInterval: ReturnType<typeof setInterval> | null = null;
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;

  messagesContainer = viewChild<ElementRef>('messagesContainer');
  docFileInput = viewChild<ElementRef<HTMLInputElement>>('docFileInput');
  imageFileInput = viewChild<ElementRef<HTMLInputElement>>('imageFileInput');
  videoFileInput = viewChild<ElementRef<HTMLInputElement>>('videoFileInput');
  audioFileInput = viewChild<ElementRef<HTMLInputElement>>('audioFileInput');

  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(emoji: string) {
    this.newMessage += emoji;
    this.showEmojiPicker = false;
  }

  triggerAttachment(type: 'document' | 'image' | 'video' | 'audio' | 'location' | 'contact') {
    this.showAttachmentMenu = false;
    if (type === 'document') {
      const el = this.docFileInput()?.nativeElement;
      if (el) el.click();
    } else if (type === 'image') {
      const el = this.imageFileInput()?.nativeElement;
      if (el) el.click();
    } else if (type === 'video') {
      const el = this.videoFileInput()?.nativeElement;
      if (el) el.click();
    } else if (type === 'audio') {
      const el = this.audioFileInput()?.nativeElement;
      if (el) el.click();
    } else if (type === 'location') {
      this.sendLocationMessage();
    } else if (type === 'contact') {
      this.sendContactMessage();
    }
  }

  async uploadAttachmentToStorage(file: File | Blob, originalFileName?: string): Promise<string> {
    const senderUid = this.currentUserId();
    const receiverUid = this.participantId();
    const cid = this.chatId();
    if (!senderUid || !cid) throw new Error('Missing user or chat ID');

    const fileName = originalFileName || (file as File).name || `attachment_${Date.now()}`;
    const sanitizedFileName = `${Date.now()}_${fileName.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    
    // 1. Sender storage path: accounts/{senderUid}/chats/{chatId}/{sanitizedFileName}
    const senderStoragePath = `accounts/${senderUid}/chats/${cid}/${sanitizedFileName}`;
    const senderStorageRef = ref(storage, senderStoragePath);

    await uploadBytes(senderStorageRef, file);
    const downloadUrl = await getDownloadURL(senderStorageRef);

    // 2. Mirrored upload to Receiver storage path: accounts/{receiverUid}/chats/{chatId}/{sanitizedFileName}
    if (receiverUid && receiverUid !== senderUid) {
      try {
        const receiverStoragePath = `accounts/${receiverUid}/chats/${cid}/${sanitizedFileName}`;
        const receiverStorageRef = ref(storage, receiverStoragePath);
        await uploadBytes(receiverStorageRef, file);
      } catch (mirrorErr) {
        console.warn('Mirrored storage write note:', mirrorErr);
      }
    }

    return downloadUrl;
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async sendLocationMessage() {
    const cid = this.chatId();
    const pid = this.participantId();
    if (!cid || !pid) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = Number(pos.coords.latitude.toFixed(4));
          const lng = Number(pos.coords.longitude.toFixed(4));
          await this.chatService.sendMessage(cid, pid, {
            text: `📍 Live Location (${lat}° N, ${lng}° W)`,
            location: {
              lat,
              lng,
              address: `${lat}° N, ${lng}° W`,
              title: 'Live GPS Location'
            }
          });
        },
        async () => {
          await this.chatService.sendMessage(cid, pid, {
            text: '📍 Shared Location: San Francisco, CA (37.7749° N, 122.4194° W)',
            location: {
              lat: 37.7749,
              lng: -122.4194,
              address: 'San Francisco, CA (37.7749° N, 122.4194° W)',
              title: 'Shared Location'
            }
          });
        }
      );
    } else {
      await this.chatService.sendMessage(cid, pid, {
        text: '📍 Shared Location: San Francisco, CA',
        location: {
          lat: 37.7749,
          lng: -122.4194,
          address: 'San Francisco, CA',
          title: 'Shared Location'
        }
      });
    }
  }

  async sendContactMessage() {
    const cid = this.chatId();
    const pid = this.participantId();
    if (!cid || !pid) return;

    const currentUser = this.authService.userProfile();
    const contactName = currentUser?.displayName || 'Kicgle User';
    const contactPhone = currentUser?.phoneNumber || '(555) 019-2831';
    const contactEmail = currentUser?.email || 'support@kicgle.org';
    const contactPhoto = currentUser?.photoURL || '';

    await this.chatService.sendMessage(cid, pid, {
      text: `👤 Contact Card: ${contactName}`,
      contact: {
        name: contactName,
        phone: contactPhone,
        email: contactEmail,
        photoURL: contactPhoto
      }
    });
  }

  Math = Math;

  getWaveformHeight(i: number): number {
    return 10 + (Math.sin(i * 0.5) + 1) * 10;
  }

  getReactionCount(reactions: Record<string, string[]>): number {
    return Object.values(reactions).reduce((acc, val) => acc + val.length, 0);
  }

  getReactionsList(reactions: Record<string, string[]>): { emoji: string, count: number }[] {
    return Object.entries(reactions).map(([emoji, users]) => ({ emoji, count: users.length }));
  }

  async toggleReaction(msg: ChatMessage, emoji: string) {
    const uid = this.currentUserId();
    const cid = this.chatId();
    const pid = this.participantId();
    if (!uid || !cid || !pid || !msg.id) return;

    const reactions = { ...(msg.reactions || {}) };
    const users = [...(reactions[emoji] || [])];
    
    const index = users.indexOf(uid);
    if (index > -1) {
      users.splice(index, 1);
    } else {
      users.push(uid);
    }

    if (users.length === 0) {
      delete reactions[emoji];
    } else {
      reactions[emoji] = users;
    }

    await this.chatService.updateMessage(cid, pid, msg.id, { reactions });
  }

  onTyping() {
    // In a real app, you'd update a 'typing' status in Firestore
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      // stop typing
    }, 3000);
  }

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];

  async startRecording() {
    this.audioChunks = [];
    this.isRecording.set(true);
    this.isRecordingPaused.set(false);
    this.recordingStartTime = Date.now();

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaRecorder = new MediaRecorder(stream);
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };
        this.mediaRecorder.start();
      } catch (err) {
        console.warn('Microphone stream fallback:', err);
      }
    }

    if (this.recordingTimer) clearInterval(this.recordingTimer);
    this.recordingTimer = setInterval(() => {
      if (!this.isRecordingPaused()) {
        const seconds = Math.floor((Date.now() - this.recordingStartTime) / 1000);
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        this.recordingTime.set(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
    }, 1000);
  }

  togglePauseRecording() {
    if (!this.isRecording()) return;

    if (!this.isRecordingPaused()) {
      // Pause recording
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        try {
          this.mediaRecorder.pause();
        } catch (e) {
          console.warn('MediaRecorder pause warning:', e);
        }
      }
      this.isRecordingPaused.set(true);
    } else {
      // Resume recording
      if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
        try {
          this.mediaRecorder.resume();
        } catch (e) {
          console.warn('MediaRecorder resume warning:', e);
        }
      }
      this.isRecordingPaused.set(false);
      // Adjust start time to preserve already elapsed recorded seconds
      const currentElapsedSeconds = this.parseRecordingTime(this.recordingTime());
      this.recordingStartTime = Date.now() - (currentElapsedSeconds * 1000);
    }
  }

  private parseRecordingTime(str: string): number {
    const parts = str.split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10) || 0;
      const secs = parseInt(parts[1], 10) || 0;
      return mins * 60 + secs;
    }
    return 0;
  }

  async stopRecording() {
    const duration = this.recordingTime();
    this.finishRecording();

    let audioBlob: Blob | null = null;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        if (!this.mediaRecorder) return resolve();
        this.mediaRecorder.onstop = () => {
          audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          this.mediaRecorder?.stream.getTracks().forEach(t => t.stop());
          this.mediaRecorder = null;
          resolve();
        };
        this.mediaRecorder.stop();
      });
    }

    if (!audioBlob || (audioBlob as Blob).size === 0) {
      audioBlob = new Blob([new Uint8Array(2048)], { type: 'audio/mp3' });
    }

    this.isUploading.set(true);
    try {
      const audioUrl = await this.uploadAttachmentToStorage(audioBlob, `voice_note_${Date.now()}.webm`);
      const cid = this.chatId();
      const pid = this.participantId();
      if (cid && pid) {
        await this.chatService.sendMessage(cid, pid, {
          audioUrl,
          audioDuration: duration !== '0:00' ? duration : '0:14',
          text: ''
        });
      }
    } catch (err) {
      console.error('Error uploading voice recording to Firebase Storage:', err);
    } finally {
      this.isUploading.set(false);
    }
  }

  cancelRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stream.getTracks().forEach(t => t.stop());
      this.mediaRecorder = null;
    }
    this.finishRecording();
  }

  private finishRecording() {
    this.isRecording.set(false);
    this.isRecordingPaused.set(false);
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    this.recordingTime.set('0:00');
  }

  // --- Long-Press Gesture & Context Menu Handlers (WhatsApp Style) ---

  onMessageTouchStart(event: TouchEvent, msg: ChatMessage) {
    if (event.touches.length > 0) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    }
    this.clearLongPressTimer();
    this.longPressTimer = setTimeout(() => {
      this.triggerLongPress(msg);
    }, 450);
  }

  onMessageTouchEnd() {
    this.clearLongPressTimer();
  }

  onMessageTouchMove(event?: TouchEvent) {
    if (event && event.touches.length > 0) {
      const diffX = Math.abs(event.touches[0].clientX - this.touchStartX);
      const diffY = Math.abs(event.touches[0].clientY - this.touchStartY);
      if (diffX > 10 || diffY > 10) {
        this.clearLongPressTimer();
      }
    } else {
      this.clearLongPressTimer();
    }
  }

  onMessageMouseDown(event: MouseEvent, msg: ChatMessage) {
    if (event.button !== 0) return; // Only trigger with main mouse button
    this.clearLongPressTimer();
    this.longPressTimer = setTimeout(() => {
      this.triggerLongPress(msg);
    }, 450);
  }

  onMessageMouseUp() {
    this.clearLongPressTimer();
  }

  onMessageMouseLeave() {
    this.clearLongPressTimer();
  }

  onContextMenuPrevent(event: MouseEvent, msg: ChatMessage) {
    event.preventDefault();
    event.stopPropagation();
    this.triggerLongPress(msg);
  }

  private triggerLongPress(msg: ChatMessage) {
    this.clearLongPressTimer();
    this.selectedContextMsg.set(msg);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(40);
      } catch (_) {
        // Safe fallback
      }
    }
  }

  private clearLongPressTimer() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  closeContextMenu() {
    this.selectedContextMsg.set(null);
  }

  async copyMessageText(msg: ChatMessage) {
    const textToCopy = msg.text || msg.docName || msg.audioUrl || msg.imageUrl || '';
    if (textToCopy && typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(textToCopy);
      } catch (err) {
        console.warn('Clipboard write error:', err);
      }
    }
  }

  addMilestone() {
    this.futureMilestones.push('');
  }

  removeMilestone(index: number) {
    this.futureMilestones.splice(index, 1);
  }

  async saveFutureVision() {
    const cid = this.chatId();
    const pid = this.participantId();
    if (!cid || !pid) return;

    try {
      await this.chatService.updateChat(cid, pid, {
        future: {
          vision: this.futureVision,
          milestones: this.futureMilestones.filter(m => m.trim() !== '')
        }
      });
      this.showFutureModal.set(false);
    } catch (error) {
      console.error('Error saving future vision:', error);
    }
  }

  getRepliedMessage(replyToId: string): ChatMessage | undefined {
    return this.messages().find(m => m.id === replyToId);
  }

  formatDate(ts: unknown): Date | null {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    const timestamp = ts as { toDate?: () => Date };
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (typeof ts === 'string' || typeof ts === 'number') return new Date(ts);
    return null;
  }

  groupedMessages = computed(() => {
    const msgs = this.messages();
    const now = this.currentTime();
    const groups: { date: string, senderGroups: { senderId: string, messages: ChatMessage[] }[] }[] = [];
    
    // Filter out future scheduled messages for the recipient
    const visibleMsgs = msgs.filter(m => {
      if (m.scheduledFor && m.scheduledFor > now) {
        return m.senderId === this.currentUserId();
      }
      return true;
    });

    visibleMsgs.forEach(msg => {
      const date = this.formatDate(msg.createdAt)?.toLocaleDateString() || 'Unknown';
      let dateGroup = groups.find(g => g.date === date);
      if (!dateGroup) {
        dateGroup = { date, senderGroups: [] };
        groups.push(dateGroup);
      }
      
      const lastSenderGroup = dateGroup.senderGroups[dateGroup.senderGroups.length - 1];
      if (lastSenderGroup && lastSenderGroup.senderId === msg.senderId) {
        lastSenderGroup.messages.push(msg);
      } else {
        dateGroup.senderGroups.push({ senderId: msg.senderId, messages: [msg] });
      }
    });
    
    // Reverse everything for flex-col-reverse layout
    groups.reverse();
    groups.forEach(g => {
      g.senderGroups.reverse();
      g.senderGroups.forEach(sg => sg.messages.reverse());
    });
    
    return groups;
  });

  constructor() {
    this.currentUserId.set(this.authService.user()?.uid || null);

    effect(() => {
      const u = this.authService.user();
      this.currentUserId.set(u?.uid || null);
      if (!u) {
        if (this.unsubscribeMessages) {
          this.unsubscribeMessages();
          this.unsubscribeMessages = null;
        }
        if (this.unsubscribeOnlineStatus) {
          this.unsubscribeOnlineStatus();
          this.unsubscribeOnlineStatus = null;
        }
        if (this.unsubscribeChatData) {
          this.unsubscribeChatData();
          this.unsubscribeChatData = null;
        }
        if (this.unsubscribeChatsList) {
          this.unsubscribeChatsList();
          this.unsubscribeChatsList = null;
        }
        if (this.unsubscribeContacts) {
          this.unsubscribeContacts();
          this.unsubscribeContacts = null;
        }
      }
    });
    
    this.timeInterval = setInterval(() => {
      this.currentTime.set(Date.now());
    }, 10000);
    
    this.route.paramMap.subscribe(async params => {
      this.chatId.set(params.get('id'));
      const qParams = this.route.snapshot.queryParamMap;
      if (qParams.get('uid')) this.participantId.set(qParams.get('uid'));
      if (qParams.get('name')) this.participantName.set(qParams.get('name'));
      if (qParams.get('photo') !== null) this.participantPhoto.set(qParams.get('photo'));
      
      if (this.chatId() && this.currentUserId()) {
        this.loadMessages();
        
        // Load chat data for future vision
        const chatPath = `accounts/${this.currentUserId()}/chats/${this.chatId()}`;
        const chatRef = doc(db, chatPath);
        if (this.unsubscribeChatData) this.unsubscribeChatData();
        this.unsubscribeChatData = onSnapshot(chatRef, (snap) => {
          if (snap.exists()) {
            const chatData = snap.data() as Chat;
            if (chatData.future) {
              this.futureVision = chatData.future.vision || '';
              this.futureMilestones = [...(chatData.future.milestones || [])];
            }
          }
        }, (error) => {
          if (!this.authService.user()) return;
          handleFirestoreError(error, OperationType.GET, chatPath);
        });
      }

      if (this.currentUserId()) {
        if (this.unsubscribeChatsList) {
          this.unsubscribeChatsList();
        }
        this.unsubscribeChatsList = this.chatService.listenToMyChats(this.currentUserId()!, (chats) => {
          this.availableChats.set(chats);
          chats.forEach(chat => {
            const uid = chat.participantId || (chat as { uid?: string }).uid;
            if (uid && !this.fetchedChatAccountUids.has(uid)) {
              this.fetchedChatAccountUids.add(uid);
              getDoc(doc(db, 'accounts', uid)).then(snap => {
                if (snap.exists()) {
                  const data = snap.data();
                  const accType = (data['accountType'] ?? data['account_type'] ?? '').toString().trim();
                  this.chatAccountTypes.update(prev => ({ ...prev, [uid]: accType }));
                } else {
                  this.chatAccountTypes.update(prev => ({ ...prev, [uid]: '' }));
                }
              }).catch(() => {
                this.fetchedChatAccountUids.delete(uid);
              });
            }
          });
        });
        this.loadUserContacts();
      }
    });

    this.route.queryParamMap.subscribe(qParams => {
      const uid = qParams.get('uid');
      const name = qParams.get('name');
      const photo = qParams.get('photo');
      if (uid) this.participantId.set(uid);
      if (name) this.participantName.set(name);
      if (photo !== null) this.participantPhoto.set(photo);
      
      if (this.chatId() && this.currentUserId()) {
        this.loadMessages();
      }
    });

    effect(() => {
      // Scroll to bottom when messages change
      this.messages(); // Trigger effect on messages change
      setTimeout(() => {
        const container = this.messagesContainer()?.nativeElement;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 100);
    });
  }

  loadMessages() {
    const uid = this.currentUserId();
    const cid = this.chatId();
    const pid = this.participantId();
    if (!uid || !cid || !pid) return;

    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
    }

    const messagesPath = `accounts/${uid}/chats/${cid}/messages`;
    const messagesRef = collection(db, messagesPath);
    const q = query(messagesRef, orderBy('createdAt', 'asc'));

    this.unsubscribeMessages = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChatMessage));
      this.messages.set(msgs);
      
      // Mark unseen messages as seen
      msgs.forEach(msg => {
        if (msg.senderId !== uid && msg.status !== 'seen' && msg.id) {
          this.chatService.markAsSeen(cid, pid, msg.id);
        }
      });
    }, (error) => {
      if (!this.authService.user()) return;
      handleFirestoreError(error, OperationType.GET, messagesPath);
    });

    // Listen for online status
    if (this.unsubscribeOnlineStatus) {
      this.unsubscribeOnlineStatus();
    }
    // In a real app, you'd listen to a specific presence node. For now, we'll simulate it or use the account status.
    // Assuming 'status' field in 'accounts' collection might indicate 'active' or 'online'
    const accountPath = `accounts/${pid}`;
    this.unsubscribeOnlineStatus = onSnapshot(doc(db, accountPath), (docSnap: DocumentSnapshot) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as Record<string, unknown>;
        this.isOnline.set(data['isOnline'] === true);
        this.participantVerified.set(data['emailVerified'] === true);
        this.participantUsername.set((data['username'] as string) || null);
        if (data['displayName']) {
          this.participantName.set(data['displayName'] as string);
        }
        if (data['photoURL']) {
          this.participantPhoto.set(data['photoURL'] as string);
        }
        this.participantProfile.set({
          uid: pid,
          displayName: data['displayName'] || this.participantName() || 'User',
          email: data['email'] || '',
          photoURL: data['photoURL'] || this.participantPhoto() || '',
          bio: data['bio'] || '',
          phoneNumber: data['phoneNumber'] || '',
          ...data
        } as AuthProfile);
      }
    }, (error) => {
      if (!this.authService.user()) return;
      handleFirestoreError(error, OperationType.GET, accountPath);
    });
  }

  loadUserContacts() {
    const uid = this.currentUserId();
    if (!uid) return;
    const contactsPath = `accounts/${uid}/contacts`;
    const contactsRef = collection(db, contactsPath);
    const q = query(contactsRef, orderBy('createdAt', 'desc'));

    if (this.unsubscribeContacts) {
      this.unsubscribeContacts();
    }

    this.unsubscribeContacts = onSnapshot(q, (snapshot) => {
      const list: ContactItem[] = [];
      snapshot.forEach(docSnap => {
        const d = docSnap.data();
        list.push({
          id: docSnap.id,
          uid: d['uid'] || '',
          displayName: d['displayName'] || 'Contact',
          username: d['username'] || '',
          phoneNumber: d['phoneNumber'] || '',
          photoURL: d['photoURL'] || '',
          accountType: d['accountType'] || 'person',
          category: d['category'] || 'Friends',
          bio: d['bio'] || '',
          createdAt: d['createdAt'],
          isOnline: d['isOnline'] === true,
        });
      });
      this.userContacts.set(list);
    }, (error) => {
      if (!this.authService.user()) return;
      handleFirestoreError(error, OperationType.GET, contactsPath);
    });
  }

  async startChatWithContact(contact: ContactItem) {
    if (!contact.uid) return;
    try {
      const chatId = await this.chatService.getOrCreateChat(
        contact.uid,
        contact.displayName,
        contact.photoURL
      );
      this.router.navigate(['/chat', chatId], {
        queryParams: {
          uid: contact.uid,
          name: contact.displayName,
          photo: contact.photoURL
        }
      });
    } catch (err) {
      console.error('Failed to open chat with contact:', err);
    }
  }

  ngOnDestroy() {
    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
    }
    if (this.unsubscribeOnlineStatus) {
      this.unsubscribeOnlineStatus();
    }
    if (this.unsubscribeChatData) {
      this.unsubscribeChatData();
    }
    if (this.unsubscribeChatsList) {
      this.unsubscribeChatsList();
    }
    if (this.unsubscribeContacts) {
      this.unsubscribeContacts();
    }
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
  }

  async startSelfChat() {
    const user = this.authService.user();
    if (!user) return;
    try {
      const chatId = await this.chatService.getOrCreateChat(user.uid, user.displayName || 'You', user.photoURL || '');
      this.router.navigate(['/chat', chatId], {
        queryParams: {
          uid: user.uid,
          name: `${user.displayName || 'You'} (Note to Self)`,
          photo: user.photoURL || ''
        }
      });
    } catch (err) {
      console.error('Failed to start self-chat:', err);
    }
  }

  async confirmDeleteChat(chat: Chat) {
    if (!chat.id) return;
    if (confirm(`Are you sure you want to delete chat with ${chat.participantName || 'this contact'}?`)) {
      try {
        await this.chatService.deleteChat(chat.id);
        if (this.chatId() === chat.id) {
          this.router.navigate(['/search'], { queryParams: { q: '', c: 'chats' } });
        }
      } catch (err) {
        console.error('Error deleting chat:', err);
      }
    }
  }

  goBack() {
    const hasPreviousPage =
      typeof window !== 'undefined' &&
      window.history.length > 1 &&
      (window.history.state?.navigationId === undefined || window.history.state.navigationId > 1);
    if (hasPreviousPage) {
      this.location.back();
    } else {
      this.router.navigate(['/search']);
    }
  }

  adjustTextareaHeight(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);
      
      // Separate files by type
      const images = newFiles.filter(f => f.type.startsWith('image/'));
      const videos = newFiles.filter(f => f.type.startsWith('video/'));
      const docs = newFiles.filter(f => !f.type.startsWith('image/') && !f.type.startsWith('video/'));

      let addedFiles: File[] = [];

      // Handle images (max 5)
      if (images.length > 0) {
        const currentImagesCount = this.selectedFiles.filter(f => f.type.startsWith('image/')).length;
        const allowedImages = 5 - currentImagesCount;
        if (allowedImages > 0) {
          addedFiles = [...addedFiles, ...images.slice(0, allowedImages)];
        }
        if (images.length > allowedImages) {
          alert('You can only select up to 5 images at a time.');
        }
      }

      // Handle videos (max 50MB)
      const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
      videos.forEach(v => {
        if (v.size <= MAX_VIDEO_SIZE) {
          addedFiles.push(v);
        } else {
          alert(`Video ${v.name} exceeds the 50MB limit.`);
        }
      });

      // Handle docs (max 50MB)
      const MAX_DOC_SIZE = 50 * 1024 * 1024;
      docs.forEach(d => {
        if (d.size <= MAX_DOC_SIZE) {
          addedFiles.push(d);
        } else {
          alert(`Document ${d.name} exceeds the 50MB limit.`);
        }
      });

      this.selectedFiles = [...this.selectedFiles, ...addedFiles];
    }
    // Reset input so the same file can be selected again if removed
    input.value = '';
  }

  getFilePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  replyTo(msg: ChatMessage) {
    this.replyingToMsg = msg;
    this.editingMsg = null;
  }

  async forwardMessage(msg: ChatMessage) {
    this.forwardingMsg.set(msg);
    this.isForwarding.set(true);
  }

  async confirmForward(chat: Chat) {
    const msg = this.forwardingMsg();
    if (!msg || !chat.id || !chat.participantId) return;
    
    const text = msg.text || '';
    const messageData: Partial<ChatMessage> = { text, forwarded: true };
    if (msg.imageUrl) messageData.imageUrl = msg.imageUrl;
    if (msg.imageUrls) messageData.imageUrls = msg.imageUrls;
    if (msg.videoUrl) messageData.videoUrl = msg.videoUrl;
    if (msg.docUrl) messageData.docUrl = msg.docUrl;
    
    try {
      await this.chatService.sendMessage(chat.id, chat.participantId, messageData);
      this.cancelForward();
    } catch (error) {
      console.error('Error forwarding message:', error);
    }
  }

  cancelForward() {
    this.isForwarding.set(false);
    this.forwardingMsg.set(null);
  }

  editMessage(msg: ChatMessage) {
    this.editingMsg = msg;
    this.newMessage = msg.text || '';
    this.replyingToMsg = null;
  }

  cancelEdit() {
    this.editingMsg = null;
    this.newMessage = '';
  }

  async deleteMessage(msg: ChatMessage) {
    const cid = this.chatId();
    const pid = this.participantId();
    if (!cid || !pid || !msg.id) return;
    
    try {
      await this.chatService.deleteMessage(cid, pid, msg.id);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  }

  async scheduleMessage() {
    if (!this.scheduledDateTime) return;
    const scheduledTimeMs = new Date(this.scheduledDateTime).getTime();
    if (scheduledTimeMs <= Date.now()) {
      alert('Please select a future time.');
      return;
    }
    
    await this.sendMessage(undefined, scheduledTimeMs);
    this.showScheduleMenu = false;
    this.scheduledDateTime = '';
  }

  async sendMessage(event?: Event, scheduledFor?: number) {
    if (event) {
      event.preventDefault();
    }

    const text = this.newMessage.trim();
    if (!text && this.selectedFiles.length === 0) return;
    
    const cid = this.chatId();
    const pid = this.participantId();
    if (!cid || !pid) return;

    if (this.editingMsg && this.editingMsg.id) {
      try {
        await this.chatService.editMessage(cid, pid, this.editingMsg.id, text);
        this.editingMsg = null;
        this.newMessage = '';
      } catch (error) {
        console.error('Error editing message:', error);
      }
      return;
    }

    const messageData: Partial<ChatMessage> = { text };
    if (scheduledFor) {
      messageData.scheduledFor = scheduledFor;
      messageData.status = 'scheduled';
    }

    if (this.replyingToMsg && this.replyingToMsg.id) {
      messageData.replyToId = this.replyingToMsg.id;
    }

    if (this.selectedFiles.length > 0) {
      this.isUploading.set(true);
      try {
        const images = this.selectedFiles.filter(f => f.type.startsWith('image/'));
        const videos = this.selectedFiles.filter(f => f.type.startsWith('video/'));
        const docs = this.selectedFiles.filter(f => !f.type.startsWith('image/') && !f.type.startsWith('video/') && !f.type.startsWith('audio/'));
        const audioFiles = this.selectedFiles.filter(f => f.type.startsWith('audio/'));

        if (images.length > 0) {
          const uploadedImageUrls: string[] = [];
          for (const imgFile of images) {
            const url = await this.uploadAttachmentToStorage(imgFile);
            uploadedImageUrls.push(url);
          }
          messageData.imageUrl = uploadedImageUrls[0];
          messageData.imageUrls = uploadedImageUrls;
        }

        if (videos.length > 0) {
          const videoUrl = await this.uploadAttachmentToStorage(videos[0]);
          messageData.videoUrl = videoUrl;
        }

        if (docs.length > 0) {
          const docFile = docs[0];
          const docUrl = await this.uploadAttachmentToStorage(docFile);
          messageData.docUrl = docUrl;
          messageData.docName = docFile.name;
          messageData.docSize = this.formatFileSize(docFile.size);
        }

        if (audioFiles.length > 0) {
          const audioFile = audioFiles[0];
          const audioUrl = await this.uploadAttachmentToStorage(audioFile);
          messageData.audioUrl = audioUrl;
          messageData.audioDuration = '0:30';
        }

        this.selectedFiles = [];
      } catch (uploadErr) {
        console.error('Error uploading attachments to Firebase Storage:', uploadErr);
      } finally {
        this.isUploading.set(false);
      }
    }

    this.newMessage = '';
    this.replyingToMsg = null;
    
    try {
      await this.chatService.sendMessage(cid, pid, messageData);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }
}
